// `new Date('YYYY-MM-DD')` parses as UTC midnight, but callers below read
// it back with local-time getters — rolling the date back a day in
// negative-UTC-offset zones. Parsing components directly avoids that.
function parseDateOnly(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(iso: string): string | null {
  const date = parseDateOnly(iso) ?? new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return formatLongDate(date);
}

export function formatLongDate(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

// Clamps to the last day of the target month (31 Jan + 1 month = 28/29 Feb),
// matching date-fns's addMonths
export function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  const day = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const lastDayOfMonth = new Date(
    result.getFullYear(),
    result.getMonth() + 1,
    0,
  ).getDate();
  result.setDate(Math.min(day, lastDayOfMonth));
  return result;
}

export function calculateAge(dateOfBirth: string): number | undefined {
  const dob = parseDateOnly(dateOfBirth) ?? new Date(dateOfBirth);
  const now = new Date();
  // A future DOB is invalid input, not "a very negative age" — without
  // this, isMinor's `age < 18` check would treat a bogus future date as
  // definitely a minor (any negative number is < 18).
  if (Number.isNaN(dob.getTime()) || dob.getTime() > now.getTime()) {
    return undefined;
  }
  let age = now.getFullYear() - dob.getFullYear();
  const hasHadBirthdayThisYear =
    now.getMonth() > dob.getMonth() ||
    (now.getMonth() === dob.getMonth() && now.getDate() >= dob.getDate());
  if (!hasHadBirthdayThisYear) {
    age -= 1;
  }
  return age;
}

const NON_MINOR_AGE = 18;

// Indeterminate DOB (invalid/unparseable) is treated as "not a minor" —
// both real call sites (form-validated submit, saved teen records) always
// pass a real DOB, so this fallback direction is a non-issue in practice.
export function isMinor(dateOfBirth: string): boolean {
  const age = calculateAge(dateOfBirth);
  return age !== undefined && age < NON_MINOR_AGE;
}
